import { useMutation, useQueryClient } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type Canvas, type CreateCanvasParams } from '~/swr-hooks/canvases/types';
import { CANVASES_LIST } from '~/swr-hooks/canvases/useListCanvases';

export const useCreateCanvas = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: CreateCanvasParams) => {
      const workspaceId = params.workspaceId ?? currentWorkspace?.id;
      invariant(workspaceId, 'Workspace ID is required');

      const apiPayload = {
        workspaceId,
        title: params.title,
        boardId: params.boardId,
        nodes: params.nodes?.map((node) => ({
          name: node.name,
          positionX: node.positionX,
          positionY: node.positionY,
          width: node.width,
          height: node.height,
          zIndex: String(node.zIndex),
          data: node.data,
        })),
      };

      return callAirApi<Canvas>(() => Canvases.create(apiPayload));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CANVASES_LIST] });
    },
    onError: (error, variables) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to create canvas',
        metadata: {
          data: {
            title: variables.title,
            boardId: variables.boardId,
          },
        },
      });
    },
  });

  return mutation;
};
